// (c) chaehongee. Confidential and proprietary.

import { useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '../../../store/hooks/hooks'
import { useMaskActions } from '../../Canvas/MaskContext'
import { selectLorasState } from '../../../store/slices/lorasSlice'
import { selectGenerationParams } from '../../../store/slices/generationSlice'
import { setBrushEnabled } from '../../../store/slices/selectionSlice'
import { buildGenerationParamsTag } from '../utils/messageUtils'
import { useSessionRefresh } from './useSessionRefresh'
import { useMaskWorkflow } from './useMaskWorkflow'
import { StreamService } from '../services/streamService'
import { appendMessage } from '../../../store/slices/chatSlice'
import { selectActiveSession, selectActiveSessionId } from '../../../store/selectors/sessionSelectors'
import { selectIsProcessingBySessionId } from '../../../store/selectors/chatSelectors'

/**
 * Hook to handle LoRA face swap operations.
 * Coordinates mask upload, session state updates, and agent streaming.
 */
export function useLoraSwap() {
    // Global State & Dispatch
    const dispatch = useAppDispatch()
    const activeSessionId = useAppSelector(selectActiveSessionId)
    const session = useAppSelector(selectActiveSession)
    const loras = useAppSelector(selectLorasState)
    const generation = useAppSelector(selectGenerationParams)
    const isProcessing = useAppSelector((s) => (
        activeSessionId ? selectIsProcessingBySessionId(s, activeSessionId) : false
    ))

    // Helpers
    const { resetMask } = useMaskActions()
    const { attachMaskToBase } = useMaskWorkflow()
    const refreshSession = useSessionRefresh(activeSessionId)

    const executeSwap = useCallback(async () => {
        // 1. Validation
        if (!session?.id || !loras.selectedId || !activeSessionId) {
            console.warn('Missing required data for swap')
            return
        }

        try {
            // 2. Mask Processing
            // Uploads mask, creates node, updates session state
            const attachment = await attachMaskToBase()
            if (!attachment) {
                console.warn('Failed to upload mask node')
                return
            }

            // 3. Agent Communication
            // Construct payload with params and user intent
            const paramsTag = buildGenerationParamsTag(generation as any)
            const message = `swap face${paramsTag}`

            // Append "swap face" to chat log immediately for feedback
            dispatch(appendMessage({
                sessionId: activeSessionId,
                message: {
                    kind: 'bubble',
                    role: 'user',
                    text: 'swap face',
                    id: crypto.randomUUID(),
                    timestamp: Date.now(),
                    pending: true
                }
            }))

            // Start streaming response from agent
            StreamService.start(
                dispatch,
                {
                    message: { type: 'text', text: message },
                    selected_nodes: attachment.payloadNodeIds,
                    session_id: session.id,
                    selected_lora_id: loras.selectedId,
                } as any,
                refreshSession
            )

            // 4. Cleanup
            // Reset canvas state for next operation
            if (resetMask) resetMask()
            dispatch(setBrushEnabled({ sessionId: activeSessionId, brushEnabled: false }))

        } catch (error) {
            console.error('Swap failed:', error)
            // StreamService handles error dispatch if it fails during stream, 
            // but if it fails before, we might need to set isProcessing false or log error.
            // Since StreamService sets isProcessing=true inside start(), we don't set it here manually beforehand?
            // Actually StreamService sets it immediately.
        }
    }, [
        activeSessionId,
        session,
        loras.selectedId,
        generation,
        dispatch,
        attachMaskToBase,
        resetMask,
        refreshSession,
    ])

    return {
        executeSwap,
        isProcessing,
    }
}
